<template>
  <div>
    <div class="candidate-application-form">
      <div class="candidate-step-menu custom-scrollbar">
        <div class="toggle-sidebar" @click="toggleSidebar">
          <div class="bar1" />
          <div class="bar2" />
          <div class="bar3" />
        </div>
        <div class="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
          <a
              v-for="(item, index) in applyFormSettings"
              class="nav-link"
              :id="`v-pills-${index}-tab`"
              :key="`step-${index}`"
          >
                        <span class="step-number">
                            {{ `${(index + 1) > 10 ? (index + 1) : `0${index + 1}`}` }}
                            <span v-if="currentIndex > index" class="complete-icon">
                                <app-icon name="check" />
                            </span>
                            <span class="step-divider"><span class="divider" /></span>
                        </span>
            <span class="step-name">
                            {{ item.title && item.title.use_custom ? item.title.custom : item.title.default }}
                        </span>
          </a>
        </div>
      </div>

      <div class="candidate-step-content">
        <div class="tab-content" id="v-pills-tabContent">
          <div class="tab-pane fade show active">
            <h4 class="mb-5">
              {{
                selectedApplyFormSettings.title && selectedApplyFormSettings.title.use_custom
                    ? selectedApplyFormSettings.title.custom
                    : selectedApplyFormSettings.title.default
              }}
            </h4>

            <div class="row">
              <div class="col-lg-9 col-xl-7">

                <!-- ✅ PAYPAL STEP RENDER -->
                <div v-if="isPaypalStep(selectedApplyFormSettings)">
                  <PaypalPayment
                      :amount="paypalAmount"
                      :currency="paypalCurrency"
                      :jobSlug="paypalJobSlug"
                      :applicantId="paypalApplicantId"
                      @payment-completed="onPaymentCompleted"
                      @payment-error="onPaymentError"
                  />

                  <!-- If applicantId missing, show helper message -->
                  <div v-if="!paypalApplicantId" class="alert alert-warning mt-3">
                    {{ $t('applicant_id_missing_for_payment') }}
                  </div>
                </div>

                <!-- ✅ NORMAL FIELD STEP RENDER -->
                <CandidateInputReceiveFields
                    v-else
                    :key="selectedApplyFormSettings.step"
                    :formBody="selectedApplyFormSettings"
                    :current-index="currentIndex"
                    @next="goNextQuesStep"
                    @previous="goPrevQuesStep"
                />

              </div>
            </div>
          </div>

          <!-- (Your long commented block stays as-is; I kept the original structure) -->

          <div class="tab-pane fade">
            <div>
              <h4 class="mb-2">
                <!-- {{ finalStep.title }} - {{ appliedDone ? $t('submitted') : $t('in_progress') }} -->
              </h4>
              <div class="row mb-4">
                <div class="col-12 min-height-300 py-primary">
                  <hr />
                  <table class="table table-borderless">
                    <tbody>
                    <tr>
                      <td class="w-50">
                        <span class="text-muted">{{ $t('first_name') }}</span>
                      </td>
                      <td class="w-50">
                        <span class="text-muted">{{ $t('last_name') }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="w-50">
                        <span class="text-muted">{{ $t('email') }}</span>
                      </td>
                      <td class="w-50">
                        <span class="text-muted">{{ $t('phone') }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="w-50" v-if="submitApplicant.gender">
                        <span class="text-muted">{{ $t('gender') }}</span>
                        <p class="fs-6">{{ submitApplicant.gender }}</p>
                      </td>
                      <td class="w-50" v-if="submitApplicant.date_of_birth">
                        <span class="text-muted">{{ $t('date_of_birth') }}</span>
                        <p class="fs-6">{{ submitApplicant.date_of_birth }}</p>
                      </td> -->
                    </tr>
                    </tbody>
                  </table>

                  <h5 class="mt-3">{{ $t('others_information') }}</h5>
                  <hr />
                </div>
              </div>
            </div>
            <div class="tab-pane-action">
              <button type="button" @click.prevent="goToQuestionLastStep">
                <app-icon name="chevron-left" class="mr-2" />
                {{ $t('previous') }}
              </button>
              <app-load-more :preloader="preloader" />
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Email Verification Modal -->
    <candidate-email-verification-modal
        v-if="emailVerificationModal"
        :job-post-id="applyFormData.id"
        @dontVerified="goToJobPostView"
        @verifiedData="afterVerifiedEmail"
    />

    <app-confirmation-modal
        v-if="showConfirmationModal"
        :icon="confirmationModalIcon"
        :hide-second-button="true"
        :title="confirmationModalTitle"
        :message="confirmationModalMessage"
        :modal-class="confirmationModalClass"
        modal-id="confirmation-modal"
        :first-button-name="$t('ok')"
        :second-button-name="''"
    />
  </div>
</template>

<script>
import CandidateInputReceiveFields from "./CandidateInputReceiveFields.vue";
import PaypalPayment from "../../Paypal/PaypalPayment.vue";

export default {
  name: "CandidateApplicationForm",
  components: { CandidateInputReceiveFields, PaypalPayment },
  props: {
    applyFormData: {}
  },
  data() {
    return {
      preloader: false,
      emailVerificationModal: false,
      showConfirmationModal: false,
      selectedApplyFormSettings: "",
      formData: [],
      currentIndex: 0
    };
  },
  computed: {
    applyFormSettings() {
      const newArr = [];

      // Build original steps exactly as your code did
      this.applyFormData.apply_form_settings.forEach((item, index) => {
        const obj = {};
        obj.step = index;
        obj.is_visible = item.isVisible;
        obj.title = {};
        obj.title.use_custom = true;
        obj.title.custom = item.title;
        obj.title.default = item.title;
        obj.icon = item.icon;
        obj.fields = [];
        item.fields.forEach((iterator, idx) => {
          obj.fields[idx] = {};
          obj.fields[idx].name = {};
          obj.fields[idx].name.label = iterator.name;
          obj.fields[idx].name.key = this.nameGen(iterator.name);
          obj.fields[idx].type = iterator.type;
          obj.fields[idx].value = "";
          obj.fields[idx].is_visible = iterator.show;
          obj.fields[idx].require = iterator.require;

          if ("custom_form" in iterator) {
            obj.fields[idx].custom_form = [];
            iterator.custom_form.forEach((subField, subIdx) => {
              console.log(subIdx);
              obj.fields[idx].custom_form[subIdx] = {};
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)] = {};
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)].label = subField.name;
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)].type = subField.type;
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)].value = "";
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)].is_visible = subField.show;
              obj.fields[idx].custom_form[subIdx][this.nameGen(subField.name)].require = subField.require;
            });
          }
        });
        newArr.push(obj);
      });

      // ✅ Inject Account step near end (before Paypal step)
      const accountStep = this.buildAccountStep();

      // ✅ Always append PayPal step right after account step (wizard-driven)
      const paypalStep = this.buildPaypalStep();

      // Put Account + PayPal at end
      newArr.push(accountStep);
      newArr.push(paypalStep);

      // Re-number steps
      newArr.forEach((s, i) => (s.step = i));

      return newArr;
    },

    // --- PayPal props: best-effort mapping from applyFormData ---
    paypalAmount() {
      // adapt to your backend fields if different
      return Number(
          this.applyFormData?.application_fee ??
          this.applyFormData?.fee ??
          this.applyFormData?.amount ??
          0
      );
    },
    paypalCurrency() {
      return this.applyFormData?.currency || "EUR";
    },
    paypalJobSlug() {
      return this.applyFormData?.slug || this.applyFormData?.job_slug || "";
    },
    paypalApplicantId() {
      // this must come from backend after creating applicant
      return Number(this.applyFormData?.applicant_id ?? this.applyFormData?.applicantId ?? 0) || 0;
    }
  },
  mounted() {
    this.selectedApplyFormSettings = this.applyFormSettings[this.currentIndex];
  },
  methods: {
    toggleSidebar() {
      $(".toggle-sidebar").toggleClass("change");
      $(".candidate-step-menu").toggleClass("active");
    },
    nameGen(name) {
      return _.snakeCase(_.lowerCase(name));
    },

    // ✅ Account Step definition
    buildAccountStep() {
      return {
        step: -1,
        is_visible: true,
        icon: "lock",
        title: {
          use_custom: true,
          custom: this.$t("account"),
          default: this.$t("account")
        },
        fields: [
          {
            name: { label: this.$t("login"), key: "login" },
            type: "text",
            value: "",
            is_visible: true,
            require: true,
            readonly: true
          },
          {
            name: { label: this.$t("password"), key: "password" },
            type: "password",
            value: "",
            is_visible: true,
            require: true
          },
          {
            name: { label: this.$t("password_verification"), key: "password_confirmation" },
            type: "password",
            value: "",
            is_visible: true,
            require: true
          }
        ]
      };
    },

    // ✅ PayPal Step definition (pure UI step)
    buildPaypalStep() {
      return {
        step: -1,
        is_visible: true,
        icon: "credit-card",
        title: {
          use_custom: true,
          custom: this.$t("payment"),
          default: this.$t("payment")
        },
        // mark this as paypal step
        is_paypal_step: true,
        fields: []
      };
    },

    isPaypalStep(stepObj) {
      return !!stepObj?.is_paypal_step;
    },

    // Grab email entered earlier in the wizard
    getEmailFromCollectedSteps() {
      for (let i = this.formData.length - 1; i >= 0; i--) {
        const step = this.formData[i];
        const fields = step?.fields || [];
        const emailField = fields.find(f => f?.name?.key === "email");
        if (emailField && emailField.value) return emailField.value;
      }
      return "";
    },

    // Fill login when entering account step
    fillLoginIfAccountStep(stepObj) {
      if (!stepObj?.fields?.length) return;
      const loginField = stepObj.fields.find(f => f?.name?.key === "login");
      if (!loginField) return;
      if (!loginField.value) {
        loginField.value = this.getEmailFromCollectedSteps();
      }
    },

    goNextQuesStep(data) {
      // store current step data
      this.formData[this.currentIndex] = data;

      this.currentIndex++;
      this.selectedApplyFormSettings = this.applyFormSettings[this.currentIndex];

      // if account step, auto fill login
      this.fillLoginIfAccountStep(this.selectedApplyFormSettings);

      // If next step is paypal and applicantId missing, request it from parent
      if (this.isPaypalStep(this.selectedApplyFormSettings) && !this.paypalApplicantId) {
        // emit collected data so parent can create applicant and return applicant_id
        this.$emit("request-applicant-id", {
          steps: this.formData,
          applyFormData: this.applyFormData
        });
      }
    },

    goPrevQuesStep() {
      if (this.currentIndex <= 0) return;

      this.formData.splice(this.currentIndex, 1);
      this.currentIndex--;
      this.selectedApplyFormSettings = this.applyFormSettings[this.currentIndex];
    },

    // PayPal callbacks
    onPaymentCompleted(payload) {
      this.$emit("payment-completed", payload);
    },
    onPaymentError(err) {
      this.$emit("payment-error", err);
    }
  }
};
</script>
